import React, { useState, useEffect } from 'react';
import { turso } from '@/database/client';

const AdminDebugDatabase: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const ADMIN_PASSWORD = 'whipt2026admin';

  useEffect(() => {
    const adminAuth = sessionStorage.getItem('whipt_admin_auth');
    if (adminAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('whipt_admin_auth', 'true');
      setIsAuthenticated(true);
    } else {
      alert('❌ Incorrect password');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] text-white flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-2xl p-8">
          <h1 className="text-3xl font-bold text-purple-300 mb-2">🔒 Admin Access</h1>
          <p className="text-slate-300 mb-6">Enter admin password to continue</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                className="w-full px-4 py-3 bg-[#2d1b4e] border border-purple-500/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-xs text-purple-300 mt-2 hover:text-purple-200"
              >
                {showPassword ? '🙈 Hide' : '👁️ Show'} password
              </button>
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-bold text-white transition-colors"
            >
              🔓 Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  const checkDatabase = async () => {
    setLoading(true);
    try {
      // Get all users
      const usersResult = await turso.execute('SELECT * FROM users ORDER BY created_at DESC LIMIT 10');
      
      // Get all user_data
      const userDataResult = await turso.execute("SELECT * FROM user_data WHERE data_type = 'age_verification' ORDER BY created_at DESC LIMIT 10");
      
      setResult({
        users: usersResult.rows,
        userData: userDataResult.rows
      });
    } catch (error: any) {
      setResult({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] text-white p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-purple-300 mb-6">🔍 Database Debug Tool</h1>
        
        <button
          onClick={checkDatabase}
          disabled={loading}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-900 rounded-lg font-bold mb-6"
        >
          {loading ? '⏳ Loading...' : '🔍 Check Database'}
        </button>

        {result && (
          <div className="space-y-6">
            {result.error && (
              <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 text-red-200">
                <strong>Error:</strong> {result.error}
              </div>
            )}

            {result.users && (
              <div className="bg-[#1a0f2e] border border-purple-400/30 rounded-lg p-6">
                <h2 className="text-xl font-bold text-purple-300 mb-4">👥 Recent Users</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-purple-500/30">
                        <th className="text-left p-2">Email</th>
                        <th className="text-left p-2">Name</th>
                        <th className="text-left p-2">ID</th>
                        <th className="text-left p-2">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.users.map((user: any, idx: number) => (
                        <tr key={idx} className="border-b border-purple-500/10">
                          <td className="p-2 font-mono text-xs">{user.email}</td>
                          <td className="p-2">{user.name}</td>
                          <td className="p-2 font-mono text-xs">{user.id}</td>
                          <td className="p-2 text-xs">{user.created_at}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.userData && (
              <div className="bg-[#1a0f2e] border border-purple-400/30 rounded-lg p-6">
                <h2 className="text-xl font-bold text-purple-300 mb-4">📋 Age Verification Data</h2>
                <div className="space-y-4">
                  {result.userData.length === 0 ? (
                    <p className="text-yellow-300">⚠️ No age verification data found!</p>
                  ) : (
                    result.userData.map((data: any, idx: number) => {
                      let parsedData;
                      try {
                        parsedData = JSON.parse(data.data_value);
                      } catch {
                        parsedData = { error: 'Could not parse' };
                      }
                      return (
                        <div key={idx} className="bg-black/30 rounded p-4 border border-purple-500/20">
                          <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                            <div><strong>User ID:</strong> <span className="font-mono text-xs">{data.user_id}</span></div>
                            <div><strong>Created:</strong> {data.created_at}</div>
                          </div>
                          <div className="mt-2">
                            <strong>Data:</strong>
                            <pre className="mt-1 p-2 bg-black/50 rounded text-xs overflow-x-auto">
                              {JSON.stringify(parsedData, null, 2)}
                            </pre>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDebugDatabase;
